/**
 * WordPress dependencies
 */
import { Button } from '@safe-wordpress/components';
import { useSelect } from '@safe-wordpress/data';
import { createInterpolateElement } from '@safe-wordpress/element';
import { _n, _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { HelpIcon } from '@nab/components';
import { store as NAB_DATA } from '@nab/data';
import { formatI18nDate } from '@nab/date';
import { computeDuration, getLetter } from '@nab/utils';
import type {
	Alternative,
	AlternativeId,
	AlternativeTrackingData,
	ECommercePlugin,
	GoalId,
	Maybe,
} from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { useExperimentAttribute } from '../hooks';
import { getMoneyLabel } from '../utils';
import { TypicalSampleSize } from './typical-sample-size';

type ExperimentStatus = 'running' | 'finished';

type Results =
	| { readonly type: 'loading' }
	| { readonly type: 'collecting-results' }
	| ( { readonly type: 'winner' } & DataDetails & Winner )
	| ( { readonly type: 'possible-winner' } & DataDetails & Winner )
	| ( { readonly type: 'no-winner' } & DataDetails );

type Winner = {
	readonly winner: {
		readonly id: AlternativeId;
		readonly name: string;
	};
};

type DataDetails = {
	readonly alternativeCount: number;
	readonly minConfidence: number;
	readonly confidence: number;
	readonly uplift: number;
	readonly estimatedImpact?: {
		readonly value: string;
		readonly uplift?: number;
	};
};

type SummaryContent = {
	eyebrow: string;
	title: string | JSX.Element;
	subtitle: string | JSX.Element;
	description: string | JSX.Element;
	icon: JSX.Element;
	primaryAction?: {
		label: string;
		onClick: () => void;
	};
	secondaryAction?: {
		label: string;
		onClick: () => void;
	};
};

export const Summary = (): JSX.Element => {
	// TODO DAVID. Review this function.
	const applyAlternative = () => undefined;
	// TODO DAVID. Review this function.
	const createFollowUpTest = () => undefined;
	// TODO DAVID. Review this function.
	const stopTest = () => undefined;

	const { status, startDate, endDate } = useExperiment();
	const results = useResults();

	const args = {
		results,
		status,
		applyAlternative,
		createFollowUpTest,
		stopTest,
	};
	const content =
		'running' === status
			? getRunningSummaryContent( args )
			: getFinishedSummaryContent( args );

	if ( ! content ) {
		return <ResultsSummarySkeleton />;
	}

	return (
		<section
			className={ `nab-summary nab-summary--${ results.type }` }
			aria-labelledby="nab-summary-title"
		>
			<div className="nab-summary__main">
				<div className="nab-summary__icon" aria-hidden="true">
					{ content.icon }
				</div>

				<div className="nab-summary__conclusion">
					<p className="nab-summary__eyebrow">{ content.eyebrow }</p>

					<h2 id="nab-summary-title" className="nab-summary__title">
						{ content.title }
					</h2>

					<p className="nab-summary__subtitle">
						{ content.subtitle }
					</p>

					<p className="nab-summary__description">
						{ content.description }
					</p>

					<TypicalSampleSize
						type={ results.type }
						confidence={
							'confidence' in results
								? results.confidence
								: undefined
						}
						minConfidence={
							'minConfidence' in results
								? results.minConfidence
								: undefined
						}
					/>
				</div>

				<SummaryMetric
					label={ _x( 'Confidence', 'text', 'nelio-ab-testing' ) }
					help={ _x(
						'How strongly the available data supports the current result.',
						'text',
						'nelio-ab-testing'
					) }
					value={ formatConfidence( results ) }
					detail={ getConfidenceLabel( results ) }
				/>

				{ hasEstimatedImpact( results ) && (
					<SummaryMetric
						label={ _x(
							'Estimated impact',
							'text',
							'nelio-ab-testing'
						) }
						help={ _x(
							'The estimated improvement associated with the leading variant.',
							'text',
							'nelio-ab-testing'
						) }
						value={ formatEstimatedImpact( results ) }
						detail={ getImpactDetail( results ) }
					/>
				) }

				<ExperimentStatusMetric
					status={ status }
					startDate={ startDate }
					endDate={ endDate }
				/>
			</div>

			{ ( content.primaryAction || content.secondaryAction ) && (
				<div className="nab-summary__actions">
					{ content.secondaryAction && (
						<Button
							className="nab-summary__button nab-summary__button--secondary"
							variant="secondary"
							onClick={ content.secondaryAction.onClick }
						>
							{ content.secondaryAction.label }
						</Button>
					) }

					{ content.primaryAction && (
						<Button
							className="nab-summary__button nab-summary__button--primary"
							variant="primary"
							onClick={ content.primaryAction.onClick }
						>
							{ content.primaryAction.label }
						</Button>
					) }
				</div>
			) }
		</section>
	);
};

type GetSummaryContentArgs = {
	readonly results: Results;
	readonly applyAlternative: ( alternativeId: string ) => void;
	readonly createFollowUpTest: () => void;
	readonly stopTest: () => void;
};

const getRunningSummaryContent = (
	args: GetSummaryContentArgs
): Maybe< SummaryContent > => {
	const { results, stopTest } = args;
	switch ( results.type ) {
		case 'loading':
			return undefined;

		case 'winner': {
			const alternativeName = results.winner.name;
			const alternativeId = results.winner.id;

			return {
				eyebrow: _x( 'Winner found', 'text', 'nelio-ab-testing' ),
				title: sprintf(
					/* translators: %s: Variant name (as in, variant X). */
					_x( '%s is winning', 'text', 'nelio-ab-testing' ),
					alternativeName
				),
				subtitle: getUpliftHeadline( results ),
				description: sprintf(
					'%1$s %2$s',
					// eslint-disable-next-line no-nested-ternary
					'control' === alternativeId
						? 2 === results.alternativeCount
							? _x(
									'The control is currently performing better than variant B.',
									'text',
									'nelio-ab-testing'
							  )
							: _x(
									'The control is currently performing better than the other variants.',
									'text',
									'nelio-ab-testing'
							  )
						: sprintf(
								/* translators: %s: Variant name (as in, variant X). */
								_x(
									'%s is performing significantly better than the control.',
									'text',
									'nelio-ab-testing'
								),
								alternativeName
						  ),
					_x(
						'We recommend stopping the test and applying it to all visitors.',
						'text',
						'nelio-ab-testing'
					)
				),
				icon: <TrophyIcon />,
				primaryAction: {
					label: _x( 'Stop Test Now', 'command', 'nelio-ab-testing' ),
					onClick: stopTest,
				},
			};
		}

		case 'possible-winner': {
			const alternativeName = results.winner.name;

			return {
				eyebrow: _x( 'Promising result', 'text', 'nelio-ab-testing' ),
				title: sprintf(
					/* translators: %s: Variant name. */
					_x( '%s is currently leading', 'text', 'nelio-ab-testing' ),
					alternativeName
				),
				subtitle: getUpliftHeadline( results ),
				description: _x(
					'The result looks promising, but more data is needed before making a final decision.',
					'text',
					'nelio-ab-testing'
				),
				icon: <TrendIcon />,
			};
		}

		case 'no-winner':
			return {
				eyebrow: _x(
					'No significant difference',
					'text',
					'nelio-ab-testing'
				),
				title: _x(
					'No significant difference found',
					'text',
					'nelio-ab-testing'
				),
				subtitle: _x(
					'None of the variants is outperforming the control',
					'text',
					'nelio-ab-testing'
				),
				description: _x(
					'We’re not detecting a reliable uplift yet. You can give it more time or try a new test.',
					'text',
					'nelio-ab-testing'
				),
				icon: <NoDifferenceIcon />,
			};

		case 'collecting-results':
			return {
				eyebrow: _x( 'Collecting results', 'text', 'nelio-ab-testing' ),
				title: _x( 'Not enough data yet', 'text', 'nelio-ab-testing' ),
				subtitle: _x(
					'The experiment is still gathering visits and conversions',
					'text',
					'nelio-ab-testing'
				),
				description: _x(
					'Keep the test running until there is enough evidence to compare the variants reliably.',
					'text',
					'nelio-ab-testing'
				),
				icon: <CollectingIcon />,
			};
	}
};

const getFinishedSummaryContent = ( args: {
	readonly results: Results;
	readonly applyAlternative: ( alternativeId: string ) => void;
	readonly createFollowUpTest: () => void;
	readonly stopTest: () => void;
} ): Maybe< SummaryContent > => {
	const { results, applyAlternative, createFollowUpTest } = args;
	switch ( results.type ) {
		case 'loading':
			return undefined;

		case 'winner': {
			const alternativeName = results.winner.name;
			const alternativeId = results.winner.id;

			return {
				eyebrow: _x( 'Winner found', 'text', 'nelio-ab-testing' ),
				title: sprintf(
					/* translators: %s: Variant name (as in, variant X). */
					_x( '%s won', 'text', 'nelio-ab-testing' ),
					alternativeName
				),
				subtitle: getUpliftHeadline( results ),
				description: sprintf(
					'%1$s %2$s',
					// eslint-disable-next-line no-nested-ternary
					'control' === alternativeId
						? 2 === results.alternativeCount
							? _x(
									'The control performed significantly better than variant B.',
									'text',
									'nelio-ab-testing'
							  )
							: _x(
									'The control performed significantly better than the other variants.',
									'text',
									'nelio-ab-testing'
							  )
						: sprintf(
								/* translators: %s: Variant name (as in, variant X). */
								_x(
									'%s performed significantly better than the control.',
									'text',
									'nelio-ab-testing'
								),
								alternativeName
						  ),
					_x(
						'We recommend stopping the test and applying it to all visitors.',
						'text',
						'nelio-ab-testing'
					)
				),
				icon: <TrophyIcon />,
				primaryAction:
					'control' === alternativeId
						? undefined
						: {
								label: sprintf(
									/* translators: %s: Variant name (as in, variant X). */
									_x(
										'Apply %s',
										'command',
										'nelio-ab-testing'
									),
									alternativeName
								),
								onClick: () =>
									applyAlternative( alternativeId ),
						  },
			};
		}

		case 'possible-winner': {
			const alternativeName = results.winner.name;

			return {
				eyebrow: _x( 'Promising result', 'text', 'nelio-ab-testing' ),
				title: sprintf(
					/* translators: %s: Variant name. */
					_x( '%s was leading', 'text', 'nelio-ab-testing' ),
					alternativeName
				),
				subtitle: getUpliftHeadline( results ),
				description: _x(
					'The result looked promising, but more data was needed before making a final decision.',
					'text',
					'nelio-ab-testing'
				),
				icon: <TrendIcon />,
				primaryAction: {
					label: _x(
						'Create Follow-Up Test',
						'command',
						'nelio-ab-testing'
					),
					onClick: createFollowUpTest,
				},
			};
		}

		case 'no-winner':
			return {
				eyebrow: _x(
					'No significant difference',
					'text',
					'nelio-ab-testing'
				),
				title: _x(
					'No significant difference found',
					'text',
					'nelio-ab-testing'
				),
				subtitle: _x(
					'None of the variants clearly outperformed the control',
					'text',
					'nelio-ab-testing'
				),
				description: _x(
					'We did not detect a reliable uplift. Try a new test.',
					'text',
					'nelio-ab-testing'
				),
				icon: <NoDifferenceIcon />,
				secondaryAction: {
					label: _x(
						'Create Follow-Up Test',
						'command',
						'nelio-ab-testing'
					),
					onClick: createFollowUpTest,
				},
			};

		case 'collecting-results':
			// NOTE. This branch will never run, because this state only occurs while the test is running.
			// Therefore, there’s no need to do anything (other than returning something so that TS doesn’t complain).
			return getRunningSummaryContent( args );
	}
};
type SummaryMetricProps = {
	label: string;
	help: string;
	value: string;
	detail?: string;
};

const SummaryMetric = ( {
	label,
	help,
	value,
	detail,
}: SummaryMetricProps ) => (
	<div className="nab-summary__metric">
		<div className="nab-summary__metric-label">
			<span>{ label }</span>
			<HelpIcon text={ help } />
		</div>

		<strong className="nab-summary__metric-value">{ value }</strong>

		{ detail && (
			<span className="nab-summary__metric-detail">
				{ createInterpolateElement( detail.replaceAll( '\n', '<br>' ), {
					br: <br />,
				} ) }
			</span>
		) }
	</div>
);

type ExperimentStatusMetricProps = {
	status: ExperimentStatus;
	startDate: string | false;
	endDate: string | false;
};

const ExperimentStatusMetric = ( {
	status,
	startDate,
	endDate,
}: ExperimentStatusMetricProps ) => {
	const start = toDate( startDate );
	const end =
		status === 'finished' && endDate ? toDate( endDate ) : new Date();

	return (
		<div className="nab-summary__metric">
			<div className="nab-summary__metric-label">Status</div>

			<span>
				<span
					className={ `nab-summary__status nab-summary__status--${ status }` }
				>
					{ status === 'running'
						? _x(
								'Running',
								'text (experiment status)',
								'nelio-ab-testing'
						  )
						: _x(
								'Finished',
								'text (experiment status)',
								'nelio-ab-testing'
						  ) }
				</span>
			</span>

			<span className="nab-summary__metric-detail">
				{ status === 'running'
					? sprintf(
							/* translators: %s: Start date. */
							_x( 'Started %s', 'text', 'nelio-ab-testing' ),
							formatI18nDate( start )
					  )
					: sprintf(
							/* translators: %s: End date. */
							_x( 'Test ended %s', 'text', 'nelio-ab-testing' ),
							formatI18nDate( end )
					  ) }
			</span>

			<span className="nab-summary__metric-detail">
				{ status === 'running'
					? sprintf(
							/* translators: %s: Day number. */
							_x( 'Day %s', 'text', 'nelio-ab-testing' ),
							getDurationInDays( start, end )
					  )
					: sprintf(
							/* translators: %s: Amount of time, like “2 months and 3 days.” */
							_x( 'Ran for %s', 'text', 'nelio-ab-testing' ),
							getBeautifulDuration( start, end )
					  ) }
			</span>
		</div>
	);
};

const ResultsSummarySkeleton = () => (
	<section
		className="nab-summary nab-summary--loading"
		aria-label="Loading result summary"
		aria-busy="true"
	>
		<div className="nab-summary__main">
			<div className="nab-summary__skeleton nab-summary__skeleton--icon" />
			<div className="nab-summary__skeleton nab-summary__skeleton--content" />
			<div className="nab-summary__skeleton nab-summary__skeleton--metric" />
			<div className="nab-summary__skeleton nab-summary__skeleton--metric" />
			<div className="nab-summary__skeleton nab-summary__skeleton--metric" />
		</div>
	</section>
);

const formatConfidence = ( results: Results ): string => {
	if ( 'loading' === results.type || 'collecting-results' === results.type ) {
		return '—';
	}

	if ( 0 === results.confidence ) {
		return '—';
	}

	return `${ Math.floor( results.confidence ) }%`;
};

const getConfidenceLabel = ( results: Results ): Maybe< string > => {
	if ( 'loading' === results.type || 'collecting-results' === results.type ) {
		return undefined;
	}

	if ( results.confidence >= 95 ) {
		return _x( 'Very high', 'text', 'nelio-ab-testing' );
	}

	if ( results.confidence >= 80 ) {
		return _x( 'High', 'text', 'nelio-ab-testing' );
	}

	if ( results.confidence >= 50 ) {
		return _x( 'Low to medium', 'text', 'nelio-ab-testing' );
	}

	return _x( 'Low', 'text', 'nelio-ab-testing' );
};

const getUpliftHeadline = ( results: Results ): string | JSX.Element => {
	if ( 'loading' === results.type || 'collecting-results' === results.type ) {
		return '';
	}

	if ( 'no-winner' === results.type || ! results.uplift ) {
		return _x(
			'Performance is being evaluated',
			'text',
			'nelio-ab-testing'
		);
	}

	if ( 'control' === results.winner.id ) {
		return '';
	}

	return createInterpolateElement(
		sprintf(
			/* translators: %s: Percentage value. */
			_x( '%s uplift over the control', 'text', 'nelio-ab-testing' ),
			`<strong>${ results.uplift.toFixed( 1 ) }%</strong>`
		),
		{ strong: <strong /> }
	);
};

function hasEstimatedImpact( results: Results ): boolean {
	if ( 'loading' === results.type || 'collecting-results' === results.type ) {
		return false;
	}
	return !! results.estimatedImpact;
}

const formatEstimatedImpact = ( results: Results ): string => {
	if ( 'loading' === results.type || 'collecting-results' === results.type ) {
		return '—';
	}

	if ( results.estimatedImpact ) {
		return results.estimatedImpact.value;
	}

	if ( results.uplift !== null && Number.isFinite( results.uplift ) ) {
		return formatSignedPercentage( results.uplift );
	}

	return '—';
};

const getImpactDetail = ( results: Results ): Maybe< string > => {
	if ( 'loading' === results.type || 'collecting-results' === results.type ) {
		return undefined;
	}

	const parts: string[] = [];

	if ( results.estimatedImpact ) {
		const period = _x( 'month', 'text', 'nelio-ab-testing' );
		parts.push( `/${ period }` );
		if (
			results.estimatedImpact.uplift &&
			Number.isFinite( results.estimatedImpact.uplift )
		) {
			parts.push(
				`(${ formatSignedPercentage(
					results.estimatedImpact.uplift
				) })`
			);
		}
	}

	return parts.join( ' ' ) || undefined;
};

const formatSignedPercentage = ( value: number ): string =>
	`${ value > 0 ? '+' : '' }${ value.toFixed( 1 ) }%`;

const getDurationInDays = ( startDate: Date, endDate: Date ): number => {
	const millisecondsPerDay = 1000 * 60 * 60 * 24;

	return Math.max(
		1,
		Math.ceil(
			( endDate.getTime() - startDate.getTime() ) / millisecondsPerDay
		)
	);
};

const getBeautifulDuration = ( startDate: Date, endDate: Date ): string => {
	const duration = computeDuration(
		startDate.toISOString(),
		endDate.toISOString()
	);

	const snippets = [
		duration.years,
		duration.months,
		duration.days,
		duration.hours,
		duration.minutes,
	];

	const { years, months, days, hours, minutes } = duration;

	const stringifiedSnippets = [
		sprintf(
			/* translators: %d: Number of years. */
			_n( '%d year', '%d years', years, 'nelio-ab-testing' ),
			years
		),
		sprintf(
			/* translators: %d: Number of months. */
			_n( '%d month', '%d months', months, 'nelio-ab-testing' ),
			months
		),
		/* translators: %d: Number of days. */
		sprintf( _n( '%d day', '%d days', days, 'nelio-ab-testing' ), days ),
		sprintf(
			/* translators: %d: Number of hours. */
			_n( '%d hour', '%d hours', hours, 'nelio-ab-testing' ),
			hours
		),
		sprintf(
			/* translators: %d: Number of minutes. */
			_n( '%d minute', '%d minutes', minutes, 'nelio-ab-testing' ),
			minutes
		),
	];

	for ( let i = 0; i < snippets.length - 1; ++i ) {
		if ( snippets[ i ] && snippets[ i + 1 ] ) {
			return joinTimes(
				stringifiedSnippets[ i ],
				stringifiedSnippets[ i + 1 ]
			);
		}

		if ( snippets[ i ] ) {
			return stringifiedSnippets[ i ] || '';
		}
	}

	if ( snippets[ snippets.length - 1 ] ) {
		return stringifiedSnippets[ snippets.length - 1 ] || '';
	}

	return _x( 'less than a minute', 'text', 'nelio-ab-testing' );
};

function joinTimes( a: string | undefined, b: string | undefined ): string {
	if ( a && b ) {
		const and = _x( 'and', 'text', 'nelio-ab-testing' );
		return `${ a } ${ and } ${ b }`;
	}

	return a || b || '';
}
const toDate = ( value: string | false ): Date =>
	value ? new Date( value ) : new Date();

const TrophyIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="M8 4h8v3.5a4 4 0 0 1-8 0V4Zm0 1H5v2a4 4 0 0 0 3.4 4M16 5h3v2a4 4 0 0 1-3.4 4M12 12v4m-3 3h6m-5-3h4"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const TrendIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="m4 16 5-5 4 4 7-8m-5 0h5v5"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const NoDifferenceIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="m6 6 12 12M18 6 6 18"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
	</svg>
);

const CollectingIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="M5 18V9m7 9V5m7 13v-6"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
	</svg>
);

// =====
// HOOKS
// =====

const useResults = () =>
	useSelect( ( select ): Results => {
		const activeExperiment = select( NAB_DATA ).getPageAttribute(
			'editor/activeExperiment'
		);
		const isLoading = ! select( NAB_DATA ).hasFinishedResolution(
			'getExperimentResults',
			[ activeExperiment ]
		);
		if ( ! activeExperiment || isLoading ) {
			return { type: 'loading' };
		}

		const minSampleSize =
			select( NAB_DATA ).getPluginSetting( 'minSampleSize' );
		const pageViews =
			select( NAB_DATA ).getPageViews( activeExperiment ) || 0;

		// TODO DAVID. Remove false.
		if ( false && pageViews < minSampleSize ) {
			return { type: 'collecting-results' };
		}

		const goals =
			select( NAB_DATA ).getExperimentAttribute(
				activeExperiment,
				'goals'
			) || [];
		const activeGoalId =
			select( NAB_DATA ).getPageAttribute( 'editor/activeGoal' ) ||
			( goals[ 0 ]?.id ?? '' );
		const winners =
			select( NAB_DATA ).getWinnersInExperiment( activeExperiment );
		// TODO DAVID. Remove the OR.
		const winner = winners?.[ activeGoalId ] || {
			alternative: 1,
			confidence: 65,
		};

		const minConfidence =
			select( NAB_DATA ).getPluginSetting( 'minConfidence' );
		const confidence = winner?.confidence || 0;

		const alternatives =
			select( NAB_DATA ).getExperimentAttribute(
				activeExperiment,
				'alternatives'
			) || [];

		const isUnique =
			select( NAB_DATA ).areUniqueResultsVisible( activeExperiment );
		const resultsOfAlternatives =
			select( NAB_DATA ).getResultsOfAlternatives( activeExperiment );
		const durationInDays = getDurationInDays(
			toDate(
				select( NAB_DATA ).getExperimentAttribute(
					activeExperiment,
					'startDate'
				) || false
			),
			'finished' ===
				select( NAB_DATA ).getExperimentAttribute(
					activeExperiment,
					'status'
				)
				? toDate(
						select( NAB_DATA ).getExperimentAttribute(
							activeExperiment,
							'endDate'
						) || false
				  )
				: new Date()
		);
		const ecommerce = select( NAB_DATA ).getECommercePlugin(
			activeExperiment,
			activeGoalId
		);
		const estimatedImpact = ecommerce
			? getEstimatedImpact(
					activeGoalId,
					alternatives,
					resultsOfAlternatives,
					isUnique,
					ecommerce,
					durationInDays
			  )
			: undefined;

		const details: DataDetails = {
			alternativeCount: alternatives.length,
			minConfidence,
			confidence,
			uplift: 0,
			estimatedImpact,
		};

		const winningAlternative = alternatives[ winner?.alternative ?? 0 ];
		if ( winner === undefined || ! winningAlternative ) {
			return {
				type: 'no-winner',
				...details,
			};
		}

		const winnerResults = resultsOfAlternatives?.[ winningAlternative.id ];
		const uplift = isUnique
			? winnerResults?.uniqueImprovementFactors[ activeGoalId ] || 0
			: winnerResults?.improvementFactors[ activeGoalId ] || 0;

		return {
			type: confidence < minConfidence ? 'possible-winner' : 'winner',
			...details,
			uplift,
			winner: {
				id: winningAlternative.id,
				name: sprintf(
					/* translators: %s: Variant letter. */
					_x( 'Variant %s', 'text', 'nelio-ab-testing' ),
					getLetter( winner.alternative )
				),
			},
		};
	}, [] );

const useExperiment = () => ( {
	status:
		'running' === useExperimentAttribute( 'status' )
			? ( 'running' as const )
			: ( 'finished' as const ),
	startDate: useExperimentAttribute( 'startDate' ) || ( false as const ),
	endDate: useExperimentAttribute( 'endDate' ) || ( false as const ),
} );

// =======
// HELPERS
// =======

function getEstimatedImpact(
	goalId: GoalId,
	alternatives: ReadonlyArray< Alternative >,
	resultsOfAlternatives: Maybe<
		Record< AlternativeId, AlternativeTrackingData >
	>,
	isUnique: boolean,
	ecommerce: ECommercePlugin,
	durationInDays: number
): DataDetails[ 'estimatedImpact' ] {
	console.log( 'NAB0', {
		goalId,
		alternatives,
		resultsOfAlternatives,
		isUnique,
	} );

	if ( ! alternatives.length || ! resultsOfAlternatives ) {
		return undefined;
	}

	const ids = alternatives.map( ( a ) => a.id );
	const values = isUnique
		? ids.map(
				( id ) =>
					resultsOfAlternatives[ id ]?.uniqueValues[ goalId ] || 0
		  )
		: ids.map(
				( id ) => resultsOfAlternatives[ id ]?.values[ goalId ] || 0
		  );

	const [ controlValue = 0, ...alternativeValues ] = values;
	const bestAlternativeValue = alternativeValues.reduce(
		( r, x ) => ( x > r ? x : r ),
		0
	);
	if ( ! bestAlternativeValue ) {
		return undefined;
	}

	const impact = bestAlternativeValue - controlValue;
	if ( impact <= 0 ) {
		return undefined;
	}

	const months = durationInDays / 30;
	return {
		value: '+' + getMoneyLabel( Math.floor( impact / months ), ecommerce ),
		uplift: controlValue
			? ( 100 * bestAlternativeValue ) / controlValue
			: undefined,
	};
}
